import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Search, AlertTriangle, CheckCircle2, Receipt,
  Banknote, Building2, X,
  Zap, Shield, Lock, Upload, FileText
} from 'lucide-react';
import { localTodayStr } from '../lib/dateFormat';
import { toast } from 'sonner';
import UploadQRDialog from '../components/UploadQRDialog';
import LateEncodeDialog from '../components/LateEncodeDialog';
import AutoInjectDialog, {
  isInsufficientSubDrawerError, isShiftRequiredError,
} from '../components/shifts/AutoInjectDialog';
import { useDayPlusOne } from '../hooks/useDayPlusOne';
import ReviewDetailDialog from '../components/ReviewDetailDialog';
import CalcInput from '../components/CalcInput';
import { reportError } from '../lib/errorReporter';

// payMethod is derived from fundSource — no separate method picker needed
const FUND_METHOD_MAP = {
  cashier: 'Cash',
  safe:    'Cash',
  bank:    'Check/Bank Transfer',
  digital: 'Digital Transfer',
};

// Standard canonical labels (used if wallet has no DB `name`)
const FUND_DEFAULT_LABELS = {
  cashier: 'Cashier Drawer',
  safe:    'Physical Safe',
  digital: 'Digital / E-Wallet',
  bank:    'Bank Account',
};

function round2(n) { return Math.round(n * 100) / 100; }

export default function PaySupplierPage() {
  const { currentBranch } = useAuth();
  const today = localTodayStr();

  // Supplier list + selection
  const [suppliers, setSuppliers] = useState([]);
  const [listSearch, setListSearch] = useState('');       // supplier picker filter
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const supplierPickerRef = useRef(null);

  // Checkbox-based PO selection: { [po_id]: amount }
  const [checkedPos, setCheckedPos] = useState({});
  const [budgetInput, setBudgetInput] = useState('');     // "Payment Amount" field

  // Payment header fields
  const [payDate, setPayDate] = useState(today);
  const [payRef, setPayRef] = useState('');
  const [payMemo, setPayMemo] = useState('');
  const [fundSource, setFundSource] = useState('cashier');
  const [payPin, setPayPin] = useState('');
  const { todayClosed: paySupTodayClosed, defaultDate: paySupDefaultDate, maxDate: paySupMaxDate } = useDayPlusOne(currentBranch?.id);
  // When today is closed, bump the default to tomorrow so user can record
  // pay-supplier without being stuck on a closed Z-Report.
  useEffect(() => {
    if (paySupTodayClosed) setPayDate(prev => (prev === today ? paySupDefaultDate : prev));
  }, [paySupTodayClosed, paySupDefaultDate, today]);

  // payMethod is derived — no separate picker
  const payMethod = FUND_METHOD_MAP[fundSource] || 'Cash';

  // Fund balances + labels + visibility
  const [walletBalances, setWalletBalances] = useState({ cashier: 0, safe: 0, bank: 0, digital: 0 });
  const [walletLabels, setWalletLabels] = useState({
    cashier: FUND_DEFAULT_LABELS.cashier,
    safe: FUND_DEFAULT_LABELS.safe,
    digital: FUND_DEFAULT_LABELS.digital,
    bank: FUND_DEFAULT_LABELS.bank,
  });
  const [walletAvailable, setWalletAvailable] = useState({ cashier: true, safe: true, digital: false, bank: false });
  const [bankHidden, setBankHidden] = useState(false);

  // Processing
  const [processing, setProcessing] = useState(false);
  const [injectDialog, setInjectDialog] = useState(null);

  // PO detail modal
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedPoNumber, setSelectedPoNumber] = useState(null);

  // Post-payment receipt upload (kept from before)
  const [paidBatch, setPaidBatch] = useState(null);
  const [batchUploadOpen, setBatchUploadOpen] = useState(false);
  const [batchCurrentPoId, setBatchCurrentPoId] = useState(null);
  const [oneReceiptMode, setOneReceiptMode] = useState(false);
  const [psUploadQROpen, setPsUploadQROpen] = useState(false);
  const [psUploadPOId, setPsUploadPOId] = useState(null);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadSuppliers = useCallback(async () => {
    try {
      const params = currentBranch ? { branch_id: currentBranch.id } : {};
      const res = await api.get('/purchase-orders/payables-by-supplier', { params });
      setSuppliers(res.data || []);
    } catch { toast.error('Failed to load suppliers'); }
  }, [currentBranch]);

  const loadWallets = useCallback(async () => {
    if (!currentBranch?.id) return;
    try {
      const res = await api.get('/fund-wallets', { params: { branch_id: currentBranch.id } });
      const ws = res.data || [];
      const byType = (t) => ws.find(w => w.type === t);
      const cashierW = byType('cashier');
      const safeW = byType('safe');
      const digitalW = byType('digital');
      const bankW = byType('bank');

      setWalletBalances({
        cashier: cashierW?.balance || 0,
        safe:    safeW?.balance || 0,
        digital: digitalW?.balance || 0,
        bank:    (bankW && !bankW.balance_hidden) ? (bankW.balance || 0) : 0,
      });
      setWalletLabels({
        cashier: cashierW?.name || FUND_DEFAULT_LABELS.cashier,
        safe:    safeW?.name    || FUND_DEFAULT_LABELS.safe,
        digital: digitalW?.name || FUND_DEFAULT_LABELS.digital,
        bank:    bankW?.name    || FUND_DEFAULT_LABELS.bank,
      });
      setWalletAvailable({
        cashier: !!cashierW,
        safe:    !!safeW,
        digital: !!digitalW,
        bank:    !!bankW,
      });
      setBankHidden(!!bankW?.balance_hidden);
    } catch {}
  }, [currentBranch]);

  useEffect(() => { loadSuppliers(); loadWallets(); }, [loadSuppliers, loadWallets]);

  // Close supplier picker on outside click
  useEffect(() => {
    if (!supplierPickerOpen) return;
    const handler = (e) => {
      if (supplierPickerRef.current && !supplierPickerRef.current.contains(e.target)) {
        setSupplierPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [supplierPickerOpen]);

  // ── Supplier selection ────────────────────────────────────────────────────

  const selectSupplier = (sup) => {
    setSelected(sup);
    setCheckedPos({});
    setBudgetInput('');
    setPayRef('');
    setPayMemo('');
    setPayPin('');
  };

  const clearSupplier = () => {
    setSelected(null);
    setCheckedPos({});
    setBudgetInput('');
  };

  // ── Derived values ────────────────────────────────────────────────────────

  const totalApplied  = Object.values(checkedPos).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const budget        = parseFloat(budgetInput) || 0;
  const unusedBudget  = budget > 0 ? round2(Math.max(0, budget - totalApplied)) : null;
  const isBankOrDig   = fundSource === 'bank' || fundSource === 'digital';
  const currentWalBal = walletBalances[fundSource] ?? 0;
  const balanceUnknown = fundSource === 'bank' && bankHidden;
  const isShort       = !balanceUnknown && totalApplied > currentWalBal && totalApplied > 0;

  // Overdue sum for a supplier (computed client-side)
  const getOverdue = (sup) =>
    sup.pos.reduce((s, po) => {
      const bal = po.balance ?? po.subtotal ?? 0;
      return (po.due_date && po.due_date < today && bal > 0) ? s + bal : s;
    }, 0);

  // ── Smart allocation ──────────────────────────────────────────────────────

  const sortedPos = (pos) =>
    [...pos].sort((a, b) => (a.due_date || '9999-12-31').localeCompare(b.due_date || '9999-12-31'));

  const autoAllocate = (budgetAmt, pos) => {
    const amt = parseFloat(budgetAmt) || 0;
    if (amt <= 0) { setCheckedPos({}); return; }
    let remaining = amt;
    const next = {};
    for (const po of sortedPos(pos)) {
      const bal = round2(po.balance ?? po.subtotal ?? 0);
      if (bal <= 0 || remaining <= 0) continue;
      const apply = round2(Math.min(remaining, bal));
      next[po.id] = apply;
      remaining = round2(remaining - apply);
    }
    setCheckedPos(next);
  };

  const handleBudgetChange = (val) => {
    setBudgetInput(val);
    if (selected) autoAllocate(val, selected.pos);
  };

  const togglePO = (po) => {
    const bal = round2(po.balance ?? po.subtotal ?? 0);
    if (checkedPos[po.id] !== undefined) {
      // Uncheck — release amount back to pool
      setCheckedPos(prev => { const next = { ...prev }; delete next[po.id]; return next; });
    } else {
      // Check — apply unused budget (or full balance if no budget set)
      const unused = budget > 0 ? round2(Math.max(0, budget - totalApplied)) : bal;
      const apply  = round2(Math.min(unused, bal));
      setCheckedPos(prev => ({ ...prev, [po.id]: apply }));
    }
  };

  const payAllDue = () => {
    if (!selected) return;
    const overduePOs = selected.pos.filter(po => po.due_date && po.due_date < today && (po.balance ?? po.subtotal ?? 0) > 0);
    const total = round2(overduePOs.reduce((s, po) => s + (po.balance ?? po.subtotal ?? 0), 0));
    setBudgetInput(total.toFixed(2));
    autoAllocate(total, selected.pos.filter(po => po.due_date && po.due_date < today));
  };

  const payAll = () => {
    if (!selected) return;
    const total = round2(selected.pos.reduce((s, po) => s + (po.balance ?? po.subtotal ?? 0), 0));
    setBudgetInput(total.toFixed(2));
    autoAllocate(total, selected.pos);
  };

  // ── Submit payment ────────────────────────────────────────────────────────

  const [lateEncodeOpen, setLateEncodeOpen] = useState(false);

  const handlePayment = async (lateEncodePayload = null) => {
    const allocations = Object.entries(checkedPos)
      .filter(([, amt]) => parseFloat(amt) > 0)
      .map(([po_id, amt]) => ({ po_id, amount: parseFloat(amt) }));

    if (allocations.length === 0) { toast.error('Select at least one PO to pay'); return; }
    if (!payPin.trim()) { toast.error('PIN or TOTP is required to authorize payment'); return; }
    if (!currentBranch?.id) { toast.error('Select a specific branch first'); return; }

    setProcessing(true);
    let totalPaid = 0, errors = [], paidPoIds = [], closedDayHit = false, errorObjects = [];

    for (const alloc of allocations) {
      // Locate the source row so we can route historical entries to the
      // dedicated pay endpoint (Phase 3.2). The payables-by-supplier
      // endpoint marks pre-system carry-forwards with `kind:'historical'`.
      const poRow = selected.pos.find(p => p.id === alloc.po_id);
      const isHistorical = poRow?.kind === 'historical';
      try {
        if (isHistorical) {
          // Historical Supplier PO — admin-PIN gated, different payload shape.
          // No `late_encode` (the entry IS the late carry-forward already).
          await api.post(`/historical-supplier-pos/${alloc.po_id}/pay`, {
            amount: alloc.amount,
            payment_method: payMethod,
            fund_source: fundSource,
            reference: payRef,
            note: payMemo,
            pin: payPin,
          });
        } else {
          await api.post(`/purchase-orders/${alloc.po_id}/pay`, {
            amount: alloc.amount, fund_source: fundSource,
            method: payMethod, reference: payRef,
            payment_date: payDate, pin: payPin,
            ...(lateEncodePayload ? { late_encode: lateEncodePayload } : {}),
          });
        }
        totalPaid += alloc.amount;
        paidPoIds.push(alloc.po_id);
      } catch (e) {
        const detail = e.response?.data?.detail;
        // Multi-shift: sub-drawer insufficient → surface AutoInjectDialog
        if (isInsufficientSubDrawerError(e)) {
          setProcessing(false);
          setInjectDialog({
            detail: e.response.data.detail,
            poId: alloc.po_id,
            poNumber: poRow?.po_number || alloc.po_id,
            vendor: selected?.vendor || 'supplier',
            retry: async (pin, reason) => {
              setProcessing(true);
              try {
                await api.post(`/purchase-orders/${alloc.po_id}/pay`, {
                  amount: alloc.amount, fund_source: fundSource,
                  method: payMethod, reference: payRef,
                  payment_date: payDate, pin: payPin,
                  auto_inject: true, inject_pin: pin, inject_reason: reason,
                  ...(lateEncodePayload ? { late_encode: lateEncodePayload } : {}),
                });
                toast.success(`Paid PO ${poRow?.po_number || alloc.po_id} with auto-inject`);
                setInjectDialog(null);
                await loadSuppliers();
                await loadWallets();
              } catch (err) {
                const d = err.response?.data?.detail;
                toast.error(typeof d === 'string' ? d : (d?.message || 'Auto-inject failed'));
              } finally {
                setProcessing(false);
              }
            },
          });
          return;
        }
        if (isShiftRequiredError(e)) {
          toast.error('Open a shift first — PO cash payment requires an active shift.');
          setProcessing(false);
          return;
        }
        if (typeof detail === 'object' && detail?.type === 'insufficient_funds') {
          toast.error(detail.message); setProcessing(false); return;
        }
        let msg;
        if (typeof detail === 'string') {
          msg = detail;
        } else if (Array.isArray(detail) && detail.length) {
          msg = detail.map(d => d.msg || JSON.stringify(d)).join('; ');
        } else if (detail && typeof detail === 'object' && detail.message) {
          msg = detail.message;
        } else {
          const status = e.response?.status;
          const network = !e.response;
          msg = network
            ? `Network error — could not reach server (${e.message || 'no response'})`
            : `Payment failed (HTTP ${status || '?'}) — ${e.response?.statusText || e.message || 'no detail'}`;
        }
        if (/already closed|late.{0,3}encode/i.test(msg) && !lateEncodePayload && !isHistorical) {
          closedDayHit = true; break;
        }
        // Store both the error object and message for the reporter
        errorObjects.push({ error: e, msg, po_id: alloc.po_id });
        errors.push(msg);
      }
    }

    setProcessing(false);

    if (closedDayHit) {
      setLateEncodeOpen(true);
      return; // Wait for dialog confirm to retry
    }
    if (errors.length) {
      // Use the error reporter so user can send details to admin
      const firstErr = errorObjects[0];
      reportError({
        error: firstErr?.error,
        page: '/pay-supplier',
        action: `Pay ${errors.length} PO(s) from ${walletLabels[fundSource]} for ${selected?.vendor}`,
        component: 'PaySupplierPage',
        userMessage: `${errors.length} payment(s) failed — an error occurred.`,
      });
    } else {
      toast.success(
        lateEncodePayload
          ? `₱${totalPaid.toFixed(2)} paid (late-encoded — appears on next open Z-Report)`
          : `₱${totalPaid.toFixed(2)} paid to ${selected.vendor} from ${walletLabels[fundSource]}`
      );
      const batchPos = allocations
        .filter(a => paidPoIds.includes(a.po_id))
        .map(a => ({ po_id: a.po_id, po_number: selected.pos.find(p => p.id === a.po_id)?.po_number || a.po_id, amount: a.amount, uploaded: false }));
      if (batchPos.length === 1) { setPsUploadPOId(batchPos[0].po_id); setPsUploadQROpen(true); }
      else { setPaidBatch({ vendor: selected.vendor, totalPaid, fundSource, fundSourceLabel: walletLabels[fundSource], pos: batchPos }); setBatchUploadOpen(true); }
    }

    setCheckedPos({});
    setBudgetInput('');
    setPayRef('');
    setPayMemo('');
    setPayPin('');
    setLateEncodeOpen(false);
    await loadSuppliers();
    await loadWallets();
    const refreshed = (await api.get('/purchase-orders/payables-by-supplier', {
      params: currentBranch ? { branch_id: currentBranch.id } : {}
    }).then(r => r.data || []).catch(() => [])).find(s => s.vendor === selected.vendor);
    setSelected(refreshed || null);
  };

  // ── Filtered lists ────────────────────────────────────────────────────────

  const filteredList = listSearch
    ? suppliers.filter(s => s.vendor.toLowerCase().includes(listSearch.toLowerCase()))
    : suppliers;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] animate-fadeIn bg-white" data-testid="pay-supplier-page">

      {/* ── Compact Header ── */}
      <div className="border-b border-slate-200 px-5 py-3 shrink-0 bg-white">

        {/* Row 1: Title + Supplier picker (popover trigger) + Total Owed */}
        <div className="flex items-center gap-4 mb-3">
          <div className="shrink-0">
            <h1 className="text-base font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Pay Supplier
            </h1>
          </div>

          {/* Supplier picker trigger + floating popover */}
          <div className="relative flex-1 min-w-0" ref={supplierPickerRef}>
            <button
              onClick={() => setSupplierPickerOpen(v => !v)}
              className={`w-full text-left rounded-xl border px-3 py-2 transition-colors ${
                selected
                  ? 'bg-[#1A4D2E]/5 border-[#1A4D2E]/30 hover:bg-[#1A4D2E]/10'
                  : 'bg-slate-50 border-slate-200 hover:border-[#1A4D2E]/40 hover:bg-slate-100'
              }`}
              data-testid="supplier-picker-trigger"
            >
              <div className="flex items-center gap-2">
                <Building2 size={14} className={selected ? 'text-[#1A4D2E] shrink-0' : 'text-slate-400 shrink-0'} />
                {selected ? (
                  <span className="text-base font-bold text-slate-800 leading-tight flex-1 truncate" style={{ fontFamily: 'Manrope' }} data-testid="supplier-display">
                    {selected.vendor}
                  </span>
                ) : (
                  <span className="text-sm text-slate-400 italic flex-1" data-testid="supplier-display">
                    Click to search supplier…
                  </span>
                )}
                {selected ? (
                  <button
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => { e.stopPropagation(); clearSupplier(); setSupplierPickerOpen(false); }}
                    className="text-slate-400 hover:text-slate-600 shrink-0 ml-1"
                    title="Clear selection"
                    data-testid="clear-supplier-btn"
                  >
                    <X size={14} />
                  </button>
                ) : (
                  <Search size={13} className="text-slate-400 shrink-0" />
                )}
              </div>
              {selected && (
                <div className="flex items-center gap-3 mt-0.5 ml-6 text-[11px] text-slate-500">
                  <span>{selected.pos.length} PO{selected.pos.length !== 1 ? 's' : ''}</span>
                  {getOverdue(selected) > 0 && (
                    <span className="text-red-600 font-medium">{formatPHP(getOverdue(selected))} overdue</span>
                  )}
                </div>
              )}
            </button>

            {/* Floating supplier picker panel */}
            {supplierPickerOpen && (
              <div
                className="absolute top-full left-0 mt-1 z-[200] w-96 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden"
                data-testid="supplier-picker-panel"
              >
                <div className="p-3 border-b border-slate-100 space-y-2">
                  <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input value={listSearch} onChange={e => setListSearch(e.target.value)}
                      placeholder="Filter suppliers..." className="pl-8 h-8 text-sm"
                      autoFocus
                      data-testid="supplier-list-search" />
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-400">
                    <span>{filteredList.length} supplier{filteredList.length !== 1 ? 's' : ''}</span>
                    <span className="font-semibold text-red-500">{formatPHP(filteredList.reduce((s, sup) => s + sup.total_owed, 0))}</span>
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {filteredList.length === 0 && (
                    <p className="text-center text-sm text-slate-400 py-8">No suppliers found</p>
                  )}
                  {filteredList.map(s => {
                    const overdue = getOverdue(s);
                    const isSelected = selected?.vendor === s.vendor;
                    return (
                      <button key={s.vendor}
                        onClick={() => { selectSupplier(s); setSupplierPickerOpen(false); }}
                        className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                          isSelected ? 'bg-[#1A4D2E]/5 border-l-2 border-l-[#1A4D2E]' : ''
                        }`}
                        data-testid={`supplier-row-${s.vendor}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-slate-800 leading-tight truncate" title={s.vendor}>{s.vendor}</p>
                          <span className="text-xs font-bold text-red-600 ml-2 shrink-0">{formatPHP(s.total_owed)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] text-slate-400">{s.pos.length} PO{s.pos.length !== 1 ? 's' : ''}</span>
                          {overdue > 0 && (
                            <Badge className="text-[9px] bg-red-100 text-red-700 px-1 py-0 h-4">
                              {formatPHP(overdue)} OVR
                            </Badge>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {selected && (
            <div className="text-right shrink-0" data-testid="supplier-total-owed">
              <p className="text-[9px] text-slate-400 uppercase tracking-wide leading-tight">Total Owed</p>
              <p className="text-2xl font-bold text-red-600 leading-tight" style={{ fontFamily: 'Manrope' }}>
                {formatPHP(selected.total_owed)}
              </p>
            </div>
          )}
        </div>

        {/* Row 2: Payment Amount + Date + Ref# | Fund source buttons — single row */}
        <div className="flex items-center gap-3">
          {/* Payment Amount */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 transition-all ${
            parseFloat(budgetInput) > 0
              ? 'bg-emerald-50 border-emerald-400 ring-2 ring-emerald-100'
              : 'bg-white border-[#1A4D2E]/40 ring-2 ring-[#1A4D2E]/10'
          }`}>
            <div className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${
              parseFloat(budgetInput) > 0 ? 'bg-emerald-500 text-white' : 'bg-[#1A4D2E] text-white'
            }`}>
              <Banknote size={16} />
            </div>
            <div className="flex flex-col">
              <Label className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide leading-none">
                Payment Amount
              </Label>
              <CalcInput
                placeholder="0.00"
                value={budgetInput}
                className="h-7 w-36 text-xl font-bold border-0 px-0 py-0 shadow-none focus-visible:ring-0"
                data-testid="budget-input"
                disabled={!selected}
                selectOnFocus
                onChange={(v) => handleBudgetChange(v)}
              />
            </div>
          </div>
          {/* Date */}
          <div className="flex items-center gap-1.5">
            <Label className="text-[10px] text-slate-400 uppercase">Date</Label>
            <Input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} max={paySupMaxDate} className="h-9 w-36" />
          </div>
          {/* Ref# */}
          <div className="flex items-center gap-1.5">
            <Label className="text-[10px] text-slate-400 uppercase">{fundSource === 'bank' ? 'Check # / Ref' : 'Ref #'}</Label>
            <Input value={payRef} onChange={e => setPayRef(e.target.value)}
              placeholder={fundSource === 'bank' ? 'Check # or bank ref' : 'Optional'}
              className="h-9 w-36" data-testid="pay-ref-input" />
          </div>
          <Separator orientation="vertical" className="h-7" />
          {/* Fund source buttons — pushed right */}
          <div className="flex gap-1 ml-auto" data-testid="fund-source-buttons">
            {[
              { key: 'cashier', lock: false },
              { key: 'safe',    lock: false },
              { key: 'digital', lock: true  },
              { key: 'bank',    lock: true  },
            ].map(f => {
              const label = walletLabels[f.key];
              const available = walletAvailable[f.key];
              const isBankHidden = f.key === 'bank' && bankHidden;
              const bal = walletBalances[f.key] ?? 0;
              const active = fundSource === f.key;
              const insuff = active && !isBankHidden && isShort;
              const disabled = !available;
              return (
                <button key={f.key} onClick={() => disabled ? null : setFundSource(f.key)}
                  disabled={disabled}
                  data-testid={`fund-${f.key}`}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs transition-all ${
                    active ? 'bg-[#1A4D2E] text-white border-[#1A4D2E] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  } ${insuff ? '!border-red-400 !bg-red-50' : ''} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  {f.lock && <Lock size={9} className={active ? 'text-emerald-300' : 'text-amber-500'} />}
                  <span className="text-[11px] font-medium">{label}</span>
                  <span className={`text-[10px] ${insuff ? 'text-red-400' : active ? 'text-emerald-200' : 'text-slate-500'}`}>
                    {isBankHidden ? '••••' : formatPHP(bal)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      {!selected ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Building2 size={48} className="mx-auto text-slate-200 mb-3" />
            <p className="text-slate-400 text-sm">Click the supplier card above to search and select a supplier</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex min-h-0 overflow-hidden">

          {/* ═══ LEFT: PO Table ═══ */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden pl-4 pr-2 pt-3 pb-3">
            <Card className="mx-0 border-slate-200 flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 shrink-0">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold" style={{ fontFamily: 'Manrope' }}>
                    Open Purchase Orders
                  </span>
                  {selected.pos.filter(po => po.due_date && po.due_date < today).length > 0 && (
                    <button onClick={payAllDue}
                      className="text-xs text-red-600 hover:underline flex items-center gap-1 font-medium"
                      data-testid="pay-all-due-btn">
                      <AlertTriangle size={11} /> Pay All Due
                    </button>
                  )}
                  <button onClick={payAll}
                    className="text-xs text-[#1A4D2E] hover:underline flex items-center gap-1 font-medium"
                    data-testid="pay-all-btn">
                    <Zap size={11} /> Pay All
                  </button>
                  {Object.keys(checkedPos).length > 0 && (
                    <button onClick={() => { setCheckedPos({}); setBudgetInput(''); }}
                      className="text-xs text-slate-400 hover:text-slate-600 hover:underline"
                      data-testid="clear-btn">
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <ScrollArea className="flex-1">
                <table className="w-full text-sm" data-testid="po-table">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="w-8 px-3 py-2"></th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">PO #</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Due</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">DR #</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Orig. Amt</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Balance</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase w-32">Paying</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPos(selected.pos).map(po => {
                      const bal = round2(po.balance ?? po.subtotal ?? 0);
                      const checked = checkedPos[po.id] !== undefined;
                      const amt = checkedPos[po.id] ?? 0;
                      const isPartial = checked && amt < bal - 0.005;
                      const isOverdue = po.due_date && po.due_date < today;
                      const isDueSoon = po.due_date && po.due_date >= today && po.due_date <= new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
                      const daysOverdue = isOverdue ? Math.floor((new Date(today) - new Date(po.due_date)) / 86400000) : 0;

                      return (
                        <tr key={po.id}
                          className={`border-b border-slate-100 transition-colors cursor-pointer ${
                            checked && !isPartial ? 'bg-emerald-50/50' :
                            isPartial ? 'bg-amber-50/50' :
                            'hover:bg-slate-50/50'
                          }`}
                          onClick={() => togglePO(po)}
                          data-testid={`po-row-${po.id}`}>

                          {/* Checkbox */}
                          <td className="px-3 py-2.5 text-center" onClick={e => { e.stopPropagation(); togglePO(po); }}>
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                              checked ? (isPartial ? 'border-amber-500 bg-amber-500' : 'border-emerald-600 bg-emerald-600') : 'border-slate-300'
                            }`}>
                              {checked && <CheckCircle2 size={10} className="text-white" strokeWidth={3} />}
                            </div>
                          </td>

                          <td className="px-3 py-2.5">
                            <button className="text-xs text-blue-600 hover:underline"
                              onClick={e => { e.stopPropagation(); setSelectedPoNumber(po.po_number); setInvoiceModalOpen(true); }}
                              data-testid={`po-link-${po.id}`}>
                              {po.po_number}
                            </button>
                            {po.kind === 'historical' && (
                              <Badge className="ml-1.5 text-[9px] bg-amber-100 text-amber-700 border-amber-200 align-middle"
                                title="Pre-system carry-forward — admin PIN required to pay"
                                data-testid={`po-row-historical-${po.id}`}>
                                Pre-system
                              </Badge>
                            )}
                          </td>

                          <td className="px-3 py-2.5 text-xs">
                            {isOverdue ? (
                              <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200">
                                {daysOverdue}d overdue
                              </Badge>
                            ) : isDueSoon ? (
                              <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200">
                                {po.due_date === today ? 'Today' : `Due ${po.due_date}`}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">{po.due_date || '—'}</span>
                            )}
                          </td>

                          <td className="px-3 py-2.5 text-xs text-slate-400">{po.dr_number || '—'}</td>

                          <td className="px-3 py-2.5">
                            <Badge className={`text-[9px] ${po.status === 'received' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                              {po.status === 'received' ? 'Received' : po.status || 'Pending'}
                            </Badge>
                          </td>

                          <td className="px-3 py-2.5 text-right text-xs text-slate-500">
                            {formatPHP(po.grand_total ?? po.subtotal ?? 0)}
                          </td>

                          <td className="px-3 py-2.5 text-right font-semibold text-sm">
                            {formatPHP(bal)}
                          </td>

                          <td className="px-3 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                            {checked ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <CalcInput value={amt === 0 ? '' : amt}
                                  placeholder="0.00"
                                  className={`h-8 w-28 text-right text-sm  ${isPartial ? 'border-amber-400 bg-amber-50' : 'border-emerald-400 bg-emerald-50'}`}
                                  onChange={(raw) => {
                                    const v = Math.min(parseFloat(raw) || 0, bal);
                                    setCheckedPos(prev => ({ ...prev, [po.id]: round2(v) }));
                                  }}
                                  data-testid={`pay-amount-${po.id}`} selectOnFocus />
                                {isPartial && <span className="text-[9px] text-amber-600 font-medium">Partial</span>}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Totals row */}
                    <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                      <td colSpan={5} className="px-3 py-2 text-right text-xs text-slate-500 uppercase">Totals</td>
                      <td className="px-3 py-2 text-right text-xs">
                        {formatPHP(selected.pos.reduce((s, p) => s + (p.grand_total ?? p.subtotal ?? 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right text-sm">{formatPHP(selected.total_owed)}</td>
                      <td className="px-3 py-2 text-right text-sm text-emerald-700">
                        {totalApplied > 0 ? formatPHP(totalApplied) : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </ScrollArea>
            </Card>
          </div>

          {/* ═══ RIGHT: Summary + Memo + PIN + Save ═══ */}
          <div className="w-72 shrink-0 border-l border-slate-200 flex flex-col gap-3 px-4 py-4 bg-slate-50/30" data-testid="payment-summary">

            {/* Amounts card */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-1.5">
              <p className="text-[10px] font-bold text-slate-700 uppercase tracking-wide mb-2" style={{ fontFamily: 'Manrope' }}>
                Amounts
              </p>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">POs Selected</span>
                <span className="font-medium">{Object.keys(checkedPos).length} of {selected.pos.length}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Total Owed (selected)</span>
                <span className="font-medium">
                  {formatPHP(selected.pos.filter(p => checkedPos[p.id] !== undefined).reduce((s, p) => s + (p.balance ?? p.subtotal ?? 0), 0))}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Applying</span>
                <span className="font-bold text-[#1A4D2E]">{formatPHP(totalApplied)}</span>
              </div>
              {unusedBudget !== null && unusedBudget > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-amber-600">Unused Budget</span>
                  <span className="font-medium text-amber-600">{formatPHP(unusedBudget)}</span>
                </div>
              )}
              <Separator className="my-1" />
              {isShort && (
                <div className="flex items-center gap-1.5 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                  <AlertTriangle size={10} />
                  Short ₱{round2(totalApplied - currentWalBal).toFixed(2)} in {fundSource}
                </div>
              )}
              <div className="flex justify-between items-baseline pt-0.5">
                <span className="text-xs font-bold text-slate-600">Remaining Balance</span>
                <span className="font-bold text-base text-red-600">
                  {formatPHP(round2(selected.total_owed - totalApplied))}
                </span>
              </div>
            </div>

            {/* Memo */}
            <div>
              <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Memo</Label>
              <Input value={payMemo} onChange={e => setPayMemo(e.target.value)}
                placeholder="Optional note (e.g. check #, batch reference)" className="h-8 mt-1 text-xs" />
            </div>

            {/* PIN + Save — pushed to bottom */}
            <div className="flex flex-col gap-1.5 mt-auto">
              <div className="relative">
                <Shield size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input type="password" autoComplete="one-time-code" data-lpignore="true"
                  value={payPin} onChange={e => setPayPin(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handlePayment(); }}
                  placeholder={isBankOrDig ? 'Admin PIN / TOTP' : 'PIN / TOTP'}
                  className="h-10 pl-8 w-full"
                  data-testid="pay-pin-input" />
              </div>
              <Button onClick={() => handlePayment()} data-testid="save-payment-btn"
                disabled={processing || totalApplied <= 0 || isShort || !payPin.trim()}
                className="h-10 w-full bg-[#1A4D2E] hover:bg-[#14532d] text-white disabled:opacity-40">
                {processing ? 'Processing...' : `Save & Pay ${totalApplied > 0 ? formatPHP(totalApplied) : ''}`}
              </Button>
              <p className="text-[9px] text-slate-400 text-center">Receipt upload required after</p>
            </div>
          </div>

        </div>
      )}

      {/* ══════════ Batch Receipt Upload Modal ══════════ */}
      {batchUploadOpen && paidBatch && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="batch-upload-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-[#1A4D2E] px-5 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>Upload Payment Receipts</p>
                  <p className="text-emerald-200 text-[11px] mt-0.5">{paidBatch.vendor} · ₱{paidBatch.totalPaid.toFixed(2)} from {paidBatch.fundSourceLabel || paidBatch.fundSource}</p>
                </div>
                <div className="text-right">
                  <p className="text-white text-xs font-semibold">{paidBatch.pos.filter(p => p.uploaded).length} / {paidBatch.pos.length}</p>
                  <p className="text-emerald-300 text-[10px]">uploaded</p>
                </div>
              </div>
              <div className="mt-2 h-1.5 bg-emerald-900 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-300 rounded-full transition-all duration-300"
                  style={{ width: `${(paidBatch.pos.filter(p => p.uploaded).length / paidBatch.pos.length) * 100}%` }} />
              </div>
            </div>
            <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
              {/* Collection receipt toggle */}
              <div className="flex items-center justify-between rounded-lg border-[var(--border-default)] bg-slate-50 px-3 py-2.5 mb-3">
                <div className="flex items-center gap-2">
                  <FileText size={13} className="text-slate-500" />
                  <div>
                    <p className="text-xs font-semibold text-slate-700">Collection Receipt</p>
                    <p className="text-[10px] text-slate-400">One receipt covers all POs</p>
                  </div>
                </div>
                <button onClick={() => setOneReceiptMode(v => !v)}
                  className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${oneReceiptMode ? 'bg-[#1A4D2E]' : 'bg-slate-300'}`}
                  data-testid="one-receipt-toggle">
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${oneReceiptMode ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {oneReceiptMode ? (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 text-[11px] text-blue-700">
                    Upload once — receipt linked to all {paidBatch.pos.length} POs automatically.
                  </div>
                  {paidBatch.pos.every(p => p.uploaded) ? (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-3">
                      <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-emerald-800">Receipt shared to all {paidBatch.pos.length} POs</p>
                        <p className="text-[10px] text-emerald-600">{paidBatch.pos.map(p => p.po_number).join(', ')}</p>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" className="w-full bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10"
                      onClick={() => setBatchCurrentPoId(paidBatch.pos[0].po_id)} data-testid="one-receipt-upload-btn">
                      <Upload size={13} className="mr-2" /> Upload Collection Receipt
                    </Button>
                  )}
                  <div className="space-y-1">
                    {paidBatch.pos.map(po => (
                      <div key={po.po_id} className="flex items-center gap-2 text-[10px] text-slate-600 px-1">
                        {po.uploaded ? <CheckCircle2 size={10} className="text-emerald-500" /> : <div className="w-2 h-2 rounded-full bg-slate-300" />}
                        <span>{po.po_number}</span>
                        <span className="text-slate-400">₱{po.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-1">Upload a receipt for each PO — required for audit trail.</p>
                  {paidBatch.pos.map((po, idx) => (
                    <div key={po.po_id}
                      className={`flex items-center justify-between rounded-xl border px-3 py-2.5 ${po.uploaded ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
                      data-testid={`batch-po-row-${po.po_id}`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${po.uploaded ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                          {po.uploaded ? <CheckCircle2 size={13} className="text-emerald-600" /> : <span className="text-[10px] font-bold text-slate-500">{idx + 1}</span>}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-700 truncate">{po.po_number}</p>
                          <p className="text-[10px] text-slate-400">₱{po.amount.toFixed(2)}</p>
                        </div>
                      </div>
                      {po.uploaded ? (
                        <div className="flex items-center gap-1 text-[10px] text-emerald-600"><CheckCircle2 size={11} /> Uploaded</div>
                      ) : (
                        <Button size="sm" variant="outline"
                          className="h-7 text-[10px] px-2.5 border-[#1A4D2E]/30 text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                          onClick={() => setBatchCurrentPoId(po.po_id)} data-testid={`batch-upload-btn-${po.po_id}`}>
                          <Upload size={10} className="mr-1" /> Upload
                        </Button>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="px-4 pb-4 pt-2 border-t border-slate-100">
              {paidBatch.pos.some(p => !p.uploaded) && (
                <p className="text-[10px] text-amber-600 flex items-center gap-1 mb-2">
                  <AlertTriangle size={10} /> {paidBatch.pos.filter(p => !p.uploaded).length} receipt(s) not yet uploaded
                </p>
              )}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 text-xs"
                  onClick={() => { if (paidBatch.pos.some(p => !p.uploaded)) toast.info('Remember to upload receipts for audit trail'); setBatchUploadOpen(false); setPaidBatch(null); setOneReceiptMode(false); }}>
                  {paidBatch.pos.every(p => p.uploaded) ? 'Done' : 'Skip Remaining'}
                </Button>
                {paidBatch.pos.every(p => p.uploaded) && (
                  <Button size="sm" className="flex-1 text-xs bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                    onClick={() => { setBatchUploadOpen(false); setPaidBatch(null); setOneReceiptMode(false); }} data-testid="batch-upload-done-btn">
                    <CheckCircle2 size={12} className="mr-1" /> All Done
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UploadQRDialog — single PO */}
      <UploadQRDialog open={psUploadQROpen}
        onClose={(count) => { setPsUploadQROpen(false); if (count > 0) toast.success(`${count} receipt photo(s) saved!`); }}
        recordType="purchase_order" recordId={psUploadPOId} />

      {/* UploadQRDialog — batch item */}
      <UploadQRDialog
        open={!!batchCurrentPoId}
        onClose={async (count) => {
          if (count > 0 && batchCurrentPoId && paidBatch) {
            if (oneReceiptMode) {
              const otherIds = paidBatch.pos.filter(p => p.po_id !== batchCurrentPoId).map(p => p.po_id);
              try {
                if (otherIds.length > 0) await api.post('/uploads/share-receipt', { source_record_id: batchCurrentPoId, target_record_ids: otherIds, record_type: 'purchase_order' });
                setPaidBatch(prev => prev ? { ...prev, pos: prev.pos.map(p => ({ ...p, uploaded: true })) } : prev);
                toast.success(`Receipt shared to all ${paidBatch.pos.length} POs`);
              } catch {
                toast.error('Uploaded but could not share to other POs — please upload individually');
                setPaidBatch(prev => prev ? { ...prev, pos: prev.pos.map(p => p.po_id === batchCurrentPoId ? { ...p, uploaded: true } : p) } : prev);
              }
            } else {
              const poNum = paidBatch.pos.find(p => p.po_id === batchCurrentPoId)?.po_number || 'PO';
              setPaidBatch(prev => prev ? { ...prev, pos: prev.pos.map(p => p.po_id === batchCurrentPoId ? { ...p, uploaded: true } : p) } : prev);
              toast.success(`Receipt saved for ${poNum}`);
            }
          }
          setBatchCurrentPoId(null);
        }}
        recordType="purchase_order" recordId={batchCurrentPoId} />

      <ReviewDetailDialog open={invoiceModalOpen} onOpenChange={setInvoiceModalOpen} poNumber={selectedPoNumber} onUpdated={loadSuppliers} showReviewAction={false} showPayAction={false} />

      <LateEncodeDialog
        open={lateEncodeOpen}
        onClose={() => setLateEncodeOpen(false)}
        orderDate={payDate}
        moduleLabel="supplier payment"
        paymentRestrictionLabel={null}
        onConfirm={({ reason, pin }) => handlePayment({ reason, pin })}
      />
      <AutoInjectDialog
        open={!!injectDialog}
        onClose={() => setInjectDialog(null)}
        onConfirm={async (pin, reason) => { if (injectDialog) await injectDialog.retry(pin, reason); }}
        subDrawerBalance={injectDialog?.detail?.sub_drawer_balance || 0}
        needed={injectDialog?.detail?.needed || 0}
        injectAmount={injectDialog?.detail?.inject_amount || 0}
        suggestedReason={injectDialog
          ? `For PO ${injectDialog.poNumber} payment — ${injectDialog.vendor}`
          : ''}
        actionLabel={`PO ${injectDialog?.poNumber || ''} payment`}
      />
    </div>
  );
}
